<template>
  <div :class="containerStyles">
  
    <label for="searchInput" class="sr-only">Search</label>
    <input
      id= "searchInput"
      type="text"
      v-model="searchQuery"
      @input="onInput"
      @keydown.down.prevent="highlightNext"
      @keydown.up.prevent="highlightPrev"
      @keydown.enter.prevent="selectHighlighted"
      :placeholder="placeholder"
      :class="[
        'flex-grow bg-primary pb-2 font-bold text-nav-s lg:text-l focus:outline-none border-b-4 pl-9 relative z-[20]',
        themeType === 'dark'
          ? 'border-orange text-white placeholder-white-50'
          : 'border-orange text-aubergine placeholder-aubergine-40',
      ]"
    />
    <clear-button
    v-if="searchQuery.length > 0"
    @handle-clear="handleClear"
    classes="pb-2 h-[18px] w-[18px] absolute right-0 z-30"
    />
    <search-button
      @click="handleSearch"
      :themeType="themeType"
      classes="mb-2 absolute z-30"
    />
    <Transition
      enter-active-class="transition-all duration-300 in-expo"
      leave-active-class="transition-all duration-300 out-expo"
      enter-from-class="opacity-0 -translate-y-[46px]"
      leave-to-class="opacity-0 -translate-y-[46px]"
    >
      <ul
        v-if="suggestions.length > 0"
        class="w-full bg-white absolute top-[45px] pl-9 z-10 py-6"
      >
        <li
          v-for="(suggestion, index) in suggestions"
          :key="index"
          @click="selectSuggestion(suggestion)"
          :class="[
            index === highlightedIndex ? 'font-bold' : '',
            'text-typography',
          ]"
        >
          {{ suggestion }}
        </li>
      </ul>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, watch } from "vue";
import ClearButton from "@atoms/SearchField/ClearButton.vue";
import SearchButton from "@atoms/SearchField/SearchButton.vue";
import { getSearchSuggestions } from "@/util/api";

export interface SearchInputProps {
  placeholder?: string;
  classes?: string;
  themeType?: "dark" | "light";
  initialValue?: string;
}

type Emits = {
  (e: "handleSearch", inputValue: string): void;
  (e: "handleClear"): void;
};
const emit = defineEmits<Emits>();

const props = withDefaults(defineProps<SearchInputProps>(), {
  placeholder: "Search",
  themeType: "dark",
  initialValue: "",
});
const { placeholder, classes, themeType, initialValue } = toRefs(props);

const searchQuery = ref(initialValue);
const suggestions = ref<string[]>([]);
const highlightedIndex = ref(-1);
const fetchSuggestionsAllowed = ref(true);

const resetSugesstions = () => {
  suggestions.value = [];
};

const handleSearch = () => {
  fetchSuggestionsAllowed.value = false; // Prevent fetching suggestions
  resetSugesstions();
  emit("handleSearch", searchQuery.value);
};

const handleClear = () => {
  searchQuery.value = "";
  emit("handleClear");
};

async function fetchSuggestions(query: string) {
  const searchSuggestions = await getSearchSuggestions(query);
  return searchSuggestions;
}

watch(searchQuery, async (newValue) => {
  if (fetchSuggestionsAllowed.value && newValue.length > 1) {
    suggestions.value = await fetchSuggestions(newValue);
  } else {
    resetSugesstions();
  }
});

function onInput() {
  highlightedIndex.value = -1; // Reset highlighted index every time the input changes
  fetchSuggestionsAllowed.value = true;
}

function highlightNext() {
  if (highlightedIndex.value < suggestions.value.length - 1) {
    highlightedIndex.value++;
  }
}

function highlightPrev() {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--;
  }
}

function selectHighlighted() {
  if (
    highlightedIndex.value >= 0 &&
    highlightedIndex.value < suggestions.value.length
  ) {
    selectSuggestion(suggestions.value[highlightedIndex.value]);
  }
  handleSearch();
}

function selectSuggestion(suggestion: string) {
  searchQuery.value = suggestion;
  resetSugesstions();
}

const containerStyles = computed(() => {
  return `flex items-center cursor-pointer ${classes?.value} relative`;
});
</script>
